export interface ResultsSummary {
  total_verified: number;
  total_invalid: number;
  total_actionable: number;
  total_generic_skipped: number;
  total_guaranteed: number;
  dm_candidates: number;
  dm_unlocked: number;
  missing_owners: number;
  has_any_data: boolean;
  ready_persons_count: number;
}

export type ListState =
  | 'empty'
  | 'ready'
  | 'all-generic'
  | 'owners-only'
  | 'verifiable';

interface ListStateResult {
  state: ListState;
  score: number;
  donutColor: string;
  donutContent: string;
}

export interface ListMetrics {
  totalVerified: number;
  totalActionable: number;
  totalGenericSkipped: number;
  dmCandidates: number;
  dmUnlocked: number;
  missingOwners: number;
  score: number;
}

export function computeListMetrics(summary: ResultsSummary): ListMetrics {
  const completedWork = summary.total_verified + summary.dm_unlocked;
  const totalWork = summary.total_verified + summary.total_actionable + summary.dm_candidates;
  const score = totalWork === 0 ? 0 : Math.round((completedWork / totalWork) * 100);
  return {
    totalVerified: summary.total_verified,
    totalActionable: summary.total_actionable,
    totalGenericSkipped: summary.total_generic_skipped,
    dmCandidates: summary.dm_candidates,
    dmUnlocked: summary.dm_unlocked,
    missingOwners: summary.missing_owners,
    score,
  };
}

export function computeListState(summary: ResultsSummary): ListStateResult {
  const metrics = computeListMetrics(summary);
  const { totalVerified, totalActionable, totalGenericSkipped, dmCandidates, dmUnlocked, missingOwners, score } = metrics;

  if (!summary.has_any_data) {
    return { state: 'empty', score: 0, donutColor: '#9CA3AF', donutContent: '—' };
  }

  if (totalActionable === 0 && missingOwners === 0 && totalVerified > 0) {
    return { state: 'ready', score: 100, donutColor: '#059669', donutContent: '100%' };
  }

  if (totalActionable === 0 && totalGenericSkipped > 0) {
    return { state: 'all-generic', score, donutColor: '#9CA3AF', donutContent: `${score}%` };
  }

  if (totalActionable === 0 && missingOwners > 0) {
    const donutContent = dmCandidates > 0 ? `${dmUnlocked}/${dmCandidates}` : `${score}%`;
    return { state: 'owners-only', score, donutColor: '#D97706', donutContent };
  }

  const donutColor = score >= 70 ? '#059669' : '#D97706';
  return { state: 'verifiable', score, donutColor, donutContent: score > 0 ? `${score}%` : 'Go!' };
}

interface StateCopy {
  full: { headline: string; sub?: string };
  short: { headline: string; sub?: string };
}

export const LIST_STATE_COPY: Record<ListState, StateCopy> = {
  empty: {
    full: { headline: 'No contact data yet' },
    short: { headline: 'No data' },
  },
  ready: {
    full: { headline: 'List is outreach-ready!' },
    short: { headline: 'Outreach-ready' },
  },
  verifiable: {
    full: { headline: 'List not outreach-ready yet', sub: 'Find & Verify emails to improve quality before exporting.' },
    short: { headline: 'Not ready yet' },
  },
  'owners-only': {
    full: { headline: 'Some owners still locked', sub: 'Find owner emails to reach decision makers directly.' },
    short: { headline: 'Owners locked' },
  },
  'all-generic': {
    full: {
      headline: 'No personally addressable emails yet',
      sub: 'All discovered emails are role-based or catch-all. Try Find owner emails to discover personal contacts.',
    },
    short: { headline: 'All role-based — try Find owners' },
  },
};

export type ReadinessState = 'pre-unlock' | 'in-progress' | 'complete';

export interface ReadinessResult {
  state: ReadinessState;
  readyCount: number;
  unlockedCount: number;
  totalDecisionMakers: number;
  readinessPct: number;
}

export function assertReadinessConsistency(summary: ResultsSummary, places: { people?: { emails?: { verification_status?: string }[] }[] }[]): void {
  if (process.env.NODE_ENV !== 'development') return;
  let pageVerifiedCount = 0;
  for (const place of places) {
    for (const person of place.people ?? []) {
      if (person.emails?.some((e) => e.verification_status === 'GUARANTEED')) {
        pageVerifiedCount++;
      }
    }
  }
  if (summary.ready_persons_count === 0 && pageVerifiedCount > 0) {
    console.warn(
      `[A1] summary.ready_persons_count=0 but page has ${pageVerifiedCount} verified persons. Cache stale or polling seam.`,
      { summary, pageVerifiedCount }
    );
  }
}

export function computeReadiness(summary: ResultsSummary): ReadinessResult {
  const readyCount = summary.ready_persons_count;
  const unlockedCount = summary.dm_unlocked;
  const totalDecisionMakers = summary.dm_candidates;

  let state: ReadinessState;
  if (unlockedCount === 0) {
    state = 'pre-unlock';
  } else if (readyCount >= unlockedCount) {
    state = 'complete';
  } else {
    state = 'in-progress';
  }

  const readinessPct = unlockedCount === 0 ? 0 : Math.round((readyCount / unlockedCount) * 100);
  return { state, readyCount, unlockedCount, totalDecisionMakers, readinessPct };
}

export function listQualityFlag(summary: ResultsSummary): string | null {
  if (summary.total_verified >= 5 && summary.total_invalid / summary.total_verified > 0.5) {
    return '⚠️ Many invalid emails — list quality is low.';
  }
  return null;
}
